import React from 'react';
import TableLoader from '../loaders/TableLoader';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Database } from 'lucide-react';

export interface TableColumn<T = unknown> {
    key: string;
    header: string;
    render?: (item: T, index: number) => React.ReactNode;
    className?: string;
    headerClassName?: string;
}

export interface Action<T> {
    text: string;
    icon: React.ReactNode;
    onClick: (row: T) => void;
}

export interface TableProps<T = unknown> {
    columns: TableColumn<T>[];
    data: T[];
    showCheckbox?: boolean;
    onSelectAll?: (checked: boolean) => void;
    onSelectItem?: (item: T, checked: boolean, index: number) => void;
    selectedItems?: Set<string | number>;
    getItemId?: (item: T) => string | number;
    className?: string;
    tableClassName?: string;
    minHeight?: string;
    emptyMessage?: string;
    loading?: boolean;
    actions?: Action<T>[] | ((item: T) => Action<T>[]);
}

const CommonTable = <T extends Record<string, any>>({
    columns,
    data,
    showCheckbox = false,
    onSelectItem,
    selectedItems = new Set(),
    getItemId = (item) => item.id,
    className = '',
    tableClassName = '',
    minHeight = '',
    emptyMessage = 'No data available',
    loading = false,
    actions,
}: TableProps<T>) => {
    return (
        <div className={`bg-card text-card-foreground rounded-xl border border-border shadow dark:border-border overflow-hidden ${minHeight} ${className}`}>
            <Table className={`${tableClassName}`}>
                <TableHeader className='*:last-child:border-b-0 bg-slate-50'>
                    <TableRow className='bg-muted/50 dark:bg-muted border-b-0! hover:bg-muted/50 dark:hover:bg-muted'>
                        {showCheckbox && <th />}
                        {columns?.map((column) => (
                            <TableHead
                                key={column.key}
                                className={`p-3 text-slate-800 text-xs font-bold uppercase ${column?.headerClassName || ''}`}
                            >
                                {column.header}
                            </TableHead>
                        ))}
                        {/* {actions && <TableHead className='p-3 text-muted-foreground text-xs text-center'>ACTIONS</TableHead>} */}
                    </TableRow>
                </TableHeader>

                <TableBody>
                    {loading ? (
                        <TableLoader
                            columns={columns?.length + (actions ? 1 : 0)}
                            rows={5}
                        />
                    ) : data?.length === 0 ? (
                        <TableRow className="hover:bg-transparent dark:hover:bg-transparent">
                            <TableCell
                                colSpan={columns.length + (showCheckbox ? 1 : 0) + (actions ? 1 : 0)}
                                className="p-12 text-center align-middle"
                            >
                                <div className="flex flex-col items-center justify-center w-full">
                                    <Database className="h-10 w-10 text-muted-foreground" />
                                    <div className="mt-3 font-medium text-base text-muted-foreground">
                                        {emptyMessage}
                                    </div>
                                </div>
                            </TableCell>
                        </TableRow>
                    ) : (
                        data?.map((item, index) => {
                            const itemId = getItemId(item);
                            const isSelected = selectedItems.has(itemId);

                            return (
                                <TableRow key={itemId || index} className="align-middle">
                                    {showCheckbox && (
                                        <TableCell className="p-3">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={(e) =>
                                                    onSelectItem?.(item, e.target.checked, index)
                                                }
                                            />
                                        </TableCell>
                                    )}

                                    {columns?.map((column) => (
                                        <TableCell
                                            key={`${itemId}-${column.key}`}
                                            className={`p-3 ${column?.className || ''}`}
                                        >
                                            {column?.render
                                                ? column.render(item, index)
                                                : item[column.key]}
                                        </TableCell>
                                    ))}

                                    {actions && (
                                        <TableCell className="p-3 justify-end">
                                            <div className="flex gap-2">
                                                {(typeof actions === 'function' ? actions(item) : actions)?.map((action, i) => (
                                                    <button
                                                        key={i}
                                                        className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                                                        onClick={() => action.onClick(item)}
                                                        title={action.text}
                                                    >
                                                        {action.icon}
                                                    </button>
                                                ))}
                                            </div>
                                        </TableCell>
                                    )}
                                </TableRow>
                            );
                        })
                    )}
                </TableBody>
            </Table>
        </div>
    );
};

export default CommonTable;
